import style from "./style.scss";
import editorialStyle from "../../components/editorial/style.scss";

import { Fragment } from "preact";
import { mergeClasses } from "../../components/helpers";
import { Button } from "../../components/link";
import Image from "../../components/image";
import NoWrap from "../../components/no-wrap";
import FixedTable from "../../components/fixed-table";

const labels = {
  price: "Price",
  capacity: "Capacity",
  capacityMax: "Max Capacity",
  dcOutput: "DC Output",
  acOutput: "AC Output",
  solar: "Solar",
  expandable: "Expansion",

  alternator: "AC Output",
  signalWire: "Capacity",
};

const formatYes = (value) => {
  return value === true ? "Yes" : "--";
};

const formatPrice = (value) => {
  if (!value) {
    return "TBD";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(value);
};

const formatNumber = (value, suffix, fallback = "--", digits = 0) => {
  return value
    ? `${new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: digits,
      }).format(value)}${suffix}`
    : fallback;
};

const formatWatts = (value) => {
  return formatNumber(value, "W");
};

const formatWh = (value, showUnits = true) => {
  const formattedNumber = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1, // Keeps things like 1.2kW clean
  }).format(value);

  return `${formattedNumber}${showUnits ? "Wh" : ""}`;
};

const formatkWh = (value, showUnits = true) => {
  const kwValue = Number(value) / 1000;

  const formattedNumber = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1, // Keeps things like 1.2kW clean
  }).format(kwValue);

  return `${formattedNumber}${showUnits ? "kWh" : ""}`;
};

const formatAmps = (value, fallback) => {
  return formatNumber(value, "A", fallback, 1);
};

const formatCaptionNumber = (product, key) => {
  if (!product?.captions) {
    return null;
  }
  const entry = product.captions[key];
  if (entry && entry.number) {
    return (
      <Fragment>
        {" "}
        <sup>{entry.number}</sup>
      </Fragment>
    );
  }
  return null;
};

const formatSolar = (product, singleLine = true) => {
  const solarKeys = ["solar", "solar2", "solar3"];

  // Check if there is ANY valid solar data at all
  const hasData = solarKeys.some((key) => product[key] && product[key] !== 0);
  if (!hasData) {
    return "--";
  }

  const renderedValues = new Set(); // Track values we've already grouped

  return (
    <Fragment>
      {solarKeys.map((key, index) => {
        const value = product[key];

        // 1. Skip if empty, 0, or already processed in a group
        if (!value || renderedValues.has(value)) return null;

        // 2. Count how many times this specific value appears in total
        const count = solarKeys.filter((k) => product[k] === value).length;

        // 3. Mark this value as rendered so we don't process it again on solar2 or solar3
        renderedValues.add(value);

        return singleLine ? (
          <Fragment key={key}>
            {/* If it's not the first thing we're rendering, add a comma */}
            {renderedValues.size > 1 && " + "}

            {formatWatts(value)}
            {count > 1 ? ` (${count}x)` : ""}
            {formatCaptionNumber(product, key)}
          </Fragment>
        ) : (
          <NoWrap key={key}>
            {/* If it's not the first thing we're rendering, add a break */}
            {renderedValues.size > 1 && <br />}

            {formatWatts(value)}
            {count > 1 ? ` (${count}x)` : ""}
            {formatCaptionNumber(product, key)}
          </NoWrap>
        );
      })}
    </Fragment>
  );
};

const formatDcOutput = (product) => {
  return (
    <Fragment>
      {product.dcOutput2 ? (
        <Fragment>
          {formatAmps(product.dcOutput, "0A")}
          {formatCaptionNumber(product, "dcOutput")},{" "}
          {formatAmps(product.dcOutput2)}
          {formatCaptionNumber(product, "dcOutput2")}
        </Fragment>
      ) : (
        <Fragment>
          {formatAmps(product.dcOutput)}
          {formatCaptionNumber(product, "dcOutput")}
        </Fragment>
      )}
    </Fragment>
  );
};

const Product = ({ product, brand, description, buttons = false }) => (
  <div class={style.overview}>
    <div class={style.img}>
      <Image
        src={product.img}
        alt={`${product.brand} ${product.name}`}
        showAlt
      />
    </div>

    <div class={style.name}>
      <div class={style.brand}>
        {brand} <span class={style.description}>{description}</span>
      </div>
      <div class={style.productName}>{product.name}</div>
      {product.highlighted ? <span class={style.highlighted} /> : null}
    </div>

    {buttons ? <Buttons product={product} /> : null}
  </div>
);

const Buttons = ({ product }) => (
  <ul class={style.bottomButtons}>
    {product.link && product.link.href ? (
      <li>
        <Button
          size="small"
          external
          affiliateData={product.link.affiliateData}
          affiliate={product.link.affiliate}
          href={product.link.href}
          icon={product.link.icon}
        >
          {product.brand}
        </Button>
      </li>
    ) : null}
    {product.amazon && product.amazon.href ? (
      <li>
        <Button
          size="small"
          external
          affiliate={product.amazon.affiliate}
          href={product.amazon.href}
          secondary
          icon="Amazon"
        >
          Amazon
        </Button>
      </li>
    ) : null}
    {product.review ? (
      <li>
        <Button
          size="small"
          secondary
          transparent
          href={product.review}
          icon="Matanich"
        >
          Review
        </Button>
      </li>
    ) : null}
  </ul>
);

const DataView = ({
  data,
  sortMessage = "Products are sorted by capacity.",
  modular = false,
}) => (
  <Fragment>
    <p class="caption-left">{sortMessage}</p>
    <div class={style.miniView}>
      {data.systems.map((product, index) => (
        <div class={style.product} key={index}>
          <div class={style.productHeader}>
            <Product
              key={product.name}
              product={product}
              brand={product.brand}
            />
            <dl class={style.price}>
              <div>
                <dt>{labels.price}</dt>
                <dd>{formatPrice(product.price)}</dd>
              </div>
            </dl>
          </div>
          <dl class={style.data}>
            <div>
              <dt>{labels.capacity}</dt>
              <dd>
                {modular
                  ? `${formatkWh(product.capacityMin, false)}-${formatkWh(product.capacityMax)}`
                  : product.expandable
                    ? `${formatWh(product.capacity)}+`
                    : formatWh(product.capacity)}
              </dd>
            </div>
            <div>
              <dt>{labels.acOutput}</dt>
              <dd>
                {formatWatts(product.acOutput)}
                {formatCaptionNumber(product, "acOutput")}
              </dd>
            </div>
            <div>
              <dt>{labels.dcOutput}</dt>
              <dd>{formatDcOutput(product)}</dd>
            </div>
            <div>
              <dt>{labels.solar}</dt>
              <dd>{formatSolar(product)}</dd>
            </div>
          </dl>
          <Buttons product={product} />
        </div>
      ))}
    </div>

    <div class={style.fullView}>
      <FixedTable>
        <table>
          <thead>
            <tr>
              <th scope="col" class={style.first}>
                Product
              </th>
              <th scope="col">{labels.price}</th>
              <th scope="col">{labels.capacity}</th>
              <th scope="col">{labels.acOutput}</th>
              <th scope="col">{labels.dcOutput}</th>
              <th scope="col">{labels.solar}</th>
              {/* {modular || <th>{labels.expandable}</th>} */}
            </tr>
          </thead>
          <tbody>
            {data.systems.map((product, index) => {
              const isNewBrand =
                index > 0 && product.brand !== data.systems[index - 1].brand;
              return (
                <tr
                  key={index}
                  class={mergeClasses([
                    editorialStyle.noHover,
                    isNewBrand ? editorialStyle.rowBorder : null,
                  ])}
                >
                  <th scope="row" class={style.cellOverview}>
                    <Product product={product} brand={product.brand} buttons />
                  </th>
                  <td>{formatPrice(product.price)}</td>
                  {/* <td>
                  {modular
                    ? `${formatKWatts(product.capacityMin, false)}-${formatKWatts(product.capacityMax)}`
                    : formatWatts(product.capacity)}
                </td> */}
                  <td>
                    {modular
                      ? `${formatkWh(product.capacityMin, false)}-${formatkWh(product.capacityMax)}`
                      : product.expandable
                        ? `${formatWh(product.capacity)}+`
                        : formatWh(product.capacity)}
                  </td>
                  <td>
                    {formatWatts(product.acOutput)}
                    {formatCaptionNumber(product, "acOutput")}
                  </td>
                  <td>{formatDcOutput(product)}</td>
                  <td>{formatSolar(product, false)}</td>
                  {/* {modular || (
                  <td>
                    {formatYes(product.expandable)}
                    {formatCaptionNumber(product, "expandable")}
                  </td>
                )} */}
                </tr>
              );
            })}
          </tbody>
        </table>
      </FixedTable>
    </div>

    <p class="caption">
      {data.captions.map((c) => (
        <Fragment key={c.number}>
          <span>
            <strong>{c.number}.</strong> {c.text}
          </span>{" "}
        </Fragment>
      ))}
    </p>
  </Fragment>
);

export default DataView;
